import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BattleshipBot,
  BattleshipGame as BattleshipEngine,
  type IBattleshipGameState,
  getShipAt,
} from '@skillarea/game-battleship';
import { BOARD_SIZE } from '@skillarea/game-battleship';

import type { TGamePlayer } from '../types';

import { BattleshipGame } from './BattleshipGame';
import styles from './BattleshipGame.module.scss';

import {
  playHitSound,
  playLoseSound,
  playMoveSound,
  playShipSunkSound,
  playWinSound,
} from '~/lib/game-sounds';
import { useUserAvatar } from '~/stores/userStore';

export interface IBattleshipGameLocalProps {
  player1: TGamePlayer;
  player2: TGamePlayer;
  turnDurationMs?: number;
  enableAI?: boolean;
  aiDifficulty?: number;
  rules: string;
  showTestSituationButtons?: boolean;
  onGameEnd?: (result: { winner: 0 | 1 | null }) => void;
  onSurrender?: () => void;
}

const DEFAULT_TURN_DURATION_MS = 5 * 60 * 1000;

function createAllFilledState(): IBattleshipGameState {
  const engine = new BattleshipEngine();
  const s = engine.getState();
  const shotsAt0 = s.shotsAt0.map((row) => row.slice());
  const shotsAt1 = s.shotsAt1.map((row) => row.slice());
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      shotsAt1[r][c] = getShipAt(s.ships1, r, c) >= 0 ? 'hit' : 'miss';
      shotsAt0[r][c] = getShipAt(s.ships0, r, c) >= 0 ? 'hit' : 'miss';
    }
  }
  return {
    ...s,
    shotsAt0,
    shotsAt1,
    sunkShips0: s.ships0.map((ship) => ship.map((cell) => ({ ...cell }))),
    sunkShips1: s.ships1.map((ship) => ship.map((cell) => ({ ...cell }))),
    currentPlayer: 0,
  };
}

export const BattleshipGameLocal: FC<IBattleshipGameLocalProps> = ({
  aiDifficulty = 0.5,
  enableAI = true,
  onGameEnd,
  onSurrender,
  player1,
  player2,
  rules,
  showTestSituationButtons = false,
  turnDurationMs = DEFAULT_TURN_DURATION_MS,
}) => {
  const avatarUrl = useUserAvatar();
  const engineRef = useRef<BattleshipEngine | null>(null);
  const currentPlayerRef = useRef<0 | 1>(0);
  const [state, setState] = useState<IBattleshipGameState | null>(null);
  const [winner, setWinner] = useState<0 | 1 | null>(null);
  const [time0LeftMs, setTime0LeftMs] = useState(turnDurationMs);
  const [time1LeftMs, setTime1LeftMs] = useState(turnDurationMs);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playedResultRef = useRef(false);

  if (state !== null) {
    currentPlayerRef.current = state.currentPlayer;
  }

  useEffect(() => {
    engineRef.current = new BattleshipEngine();
    setState(engineRef.current.getState());
    playedResultRef.current = false;
  }, []);

  const loadRandomGeneration = useCallback(() => {
    engineRef.current = new BattleshipEngine();
    setState(engineRef.current.getState());
    setWinner(null);
    setTime0LeftMs(turnDurationMs);
    setTime1LeftMs(turnDurationMs);
    playedResultRef.current = false;
  }, [turnDurationMs]);

  const loadAllFilled = useCallback(() => {
    setState(createAllFilledState());
    setWinner(null);
    setTime0LeftMs(turnDurationMs);
    setTime1LeftMs(turnDurationMs);
    playedResultRef.current = false;
  }, [turnDurationMs]);

  useEffect(() => {
    if (winner !== null || state == null) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      const cp = currentPlayerRef.current;
      if (cp === 0) {
        setTime0LeftMs((prev) => Math.max(0, prev - 1000));
      } else {
        setTime1LeftMs((prev) => Math.max(0, prev - 1000));
      }
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [winner, state == null]);

  useEffect(() => {
    if (winner !== null || state == null) return;
    if (state.currentPlayer === 0 && time0LeftMs <= 0) {
      setWinner(1);
      if (!playedResultRef.current) {
        playedResultRef.current = true;
        playLoseSound();
      }
      onGameEnd?.({ winner: 1 });
      return;
    }
    if (state.currentPlayer === 1 && time1LeftMs <= 0) {
      setWinner(0);
      if (!playedResultRef.current) {
        playedResultRef.current = true;
        playWinSound();
      }
      onGameEnd?.({ winner: 0 });
    }
  }, [time0LeftMs, time1LeftMs, state, winner, onGameEnd]);

  const playShotSound = useCallback((shipSunk: boolean, hit: boolean) => {
    if (shipSunk) playShipSunkSound();
    else if (hit) playHitSound();
    else playMoveSound();
  }, []);

  const runBotSequence = useCallback(() => {
    const step = () => {
      window.setTimeout(() => {
        if (!engineRef.current || !enableAI) return;
        const currentState = engineRef.current.getState();
        if (currentState.currentPlayer !== 1) return;

        const bot = new BattleshipBot(aiDifficulty);
        const move = bot.getMove(currentState, 1);
        const botResult = engineRef.current.shoot(1, move.row, move.col);
        if (!botResult.success) return;

        playShotSound(botResult.shipSunk, botResult.hit);
        setState(botResult.state);

        if (botResult.allSunk && botResult.winner !== null) {
          setWinner(botResult.winner);
          if (!playedResultRef.current) {
            playedResultRef.current = true;
            if (botResult.winner === 0) playWinSound();
            else playLoseSound();
          }
          onGameEnd?.({ winner: botResult.winner });
          return;
        }

        if (botResult.state.currentPlayer === 1) {
          step();
        }
      }, 600);
    };
    step();
  }, [enableAI, aiDifficulty, onGameEnd, playShotSound]);

  const handleShoot = useCallback(
    (row: number, col: number) => {
      if (!engineRef.current || !state || winner !== null) return;
      if (state.currentPlayer !== 0) return;

      const result = engineRef.current.shoot(0, row, col);
      if (!result.success) return;

      playShotSound(result.shipSunk, result.hit);
      setState(result.state);

      if (result.allSunk && result.winner !== null) {
        setWinner(result.winner);
        if (!playedResultRef.current) {
          playedResultRef.current = true;
          if (result.winner === 0) playWinSound();
          else playLoseSound();
        }
        onGameEnd?.({ winner: result.winner });
        return;
      }

      if (enableAI && result.state.currentPlayer === 1) {
        runBotSequence();
      }
    },
    [state, winner, enableAI, onGameEnd, playShotSound, runBotSequence],
  );

  const handleSurrender = useCallback(() => {
    if (!playedResultRef.current) {
      playedResultRef.current = true;
      playLoseSound();
    }
    onSurrender?.();
  }, [onSurrender]);

  if (!state) {
    return null;
  }

  const isGameOver = winner !== null;

  const content = (
    <BattleshipGame
      isGameOver={isGameOver}
      onShoot={handleShoot}
      onSurrender={handleSurrender}
      player1={{ ...player1, avatarUrl: player1.avatarUrl ?? avatarUrl }}
      player1TimeLeftMs={time0LeftMs}
      player2={player2}
      player2TimeLeftMs={time1LeftMs}
      playerSlot={0}
      rulesContent={rules}
      state={state}
      turnDurationMs={turnDurationMs}
      winner={winner}
    />
  );

  if (showTestSituationButtons) {
    return (
      <div className={styles.localRoot}>
        <div className={styles.testButtons}>
          <button
            className={styles.testPromotionButton}
            onClick={loadRandomGeneration}
            type="button"
          >
            Random generation
          </button>
          <button
            className={styles.testPromotionButton}
            onClick={loadAllFilled}
            type="button"
          >
            All cells filled
          </button>
        </div>
        {content}
      </div>
    );
  }

  return content;
};
